import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Header from '@/pages/home/components/Header';
import Footer from '@/pages/home/components/Footer';
import { useAppDownload } from '@/context/AppDownloadContext';
import { historyEntries, historyStats, type HistoryEntry } from '@/mocks/listenerHistory';

type CategoryFilter = 'todas' | 'radio' | 'programa' | 'evento' | 'concurso' | 'saludo' | 'cancion';

const categoryConfig: { key: CategoryFilter; label: string; icon: string; color: string }[] = [
  { key: 'todas', label: 'Todas', icon: 'ri-history-line', color: 'bg-foreground-600' },
  { key: 'radio', label: 'Radios', icon: 'ri-radio-line', color: 'bg-sky-500' },
  { key: 'programa', label: 'Programas', icon: 'ri-calendar-check-line', color: 'bg-violet-500' },
  { key: 'evento', label: 'Eventos', icon: 'ri-calendar-event-line', color: 'bg-emerald-500' },
  { key: 'concurso', label: 'Concursos', icon: 'ri-trophy-line', color: 'bg-amber-500' },
  { key: 'saludo', label: 'Saludos', icon: 'ri-chat-smile-2-line', color: 'bg-rose-500' },
  { key: 'cancion', label: 'Canciones', icon: 'ri-music-line', color: 'bg-pink-500' },
];

const typeMeta: Record<string, { icon: string; color: string; bg: string }> = {
  radio: { icon: 'ri-radio-line', color: 'text-sky-600', bg: 'bg-sky-100' },
  programa: { icon: 'ri-calendar-check-line', color: 'text-violet-600', bg: 'bg-violet-100' },
  evento: { icon: 'ri-calendar-event-line', color: 'text-emerald-600', bg: 'bg-emerald-100' },
  concurso: { icon: 'ri-trophy-line', color: 'text-amber-600', bg: 'bg-amber-100' },
  saludo: { icon: 'ri-chat-smile-2-line', color: 'text-rose-600', bg: 'bg-rose-100' },
  cancion: { icon: 'ri-music-line', color: 'text-pink-600', bg: 'bg-pink-100' },
};

function formatRelativeDate(dateStr: string): string {
  const now = new Date();
  const date = new Date(dateStr);
  const diffMs = now.getTime() - date.getTime();
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffHours < 1) return 'Ahora';
  if (diffHours < 24) return `Hace ${diffHours}h`;
  if (diffDays === 1) return 'Ayer';
  if (diffDays < 7) return `Hace ${diffDays} días`;
  if (diffDays < 14) return 'Hace 1 semana';
  if (diffDays < 21) return 'Hace 2 semanas';
  const weeks = Math.floor(diffDays / 7);
  return `Hace ${weeks} semanas`;
}

function formatTime(dateStr: string): string {
  return new Date(dateStr).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });
}

/** Group entries by date label for timeline separation */
function groupByDay(entries: HistoryEntry[]): { label: string; entries: HistoryEntry[] }[] {
  const groups: { label: string; entries: HistoryEntry[] }[] = [];
  let currentLabel = '';
  let currentGroup: HistoryEntry[] = [];

  for (const entry of entries) {
    const label = formatRelativeDate(entry.timestamp);
    if (label !== currentLabel) {
      if (currentGroup.length > 0) {
        groups.push({ label: currentLabel, entries: currentGroup });
      }
      currentLabel = label;
      currentGroup = [entry];
    } else {
      currentGroup.push(entry);
    }
  }
  if (currentGroup.length > 0) {
    groups.push({ label: currentLabel, entries: currentGroup });
  }
  return groups;
}

export default function Historial() {
  const { openModal: openDownloadModal } = useAppDownload();
  const [activeFilter, setActiveFilter] = useState<CategoryFilter>('todas');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const filteredEntries = useMemo(() => {
    const sorted = [...historyEntries].sort(
      (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
    );
    if (activeFilter === 'todas') return sorted;
    return sorted.filter((e) => e.type === activeFilter);
  }, [activeFilter]);

  const dayGroups = useMemo(() => groupByDay(filteredEntries), [filteredEntries]);

  const filteredStats = useMemo(() => {
    if (activeFilter === 'todas') return { count: historyEntries.length, label: 'actividades' };
    const cfg = categoryConfig.find((c) => c.key === activeFilter);
    return {
      count: historyEntries.filter((e) => e.type === activeFilter).length,
      label: cfg?.label.toLowerCase() || '',
    };
  }, [activeFilter]);

  return (
    <div className="min-h-screen bg-background-100/50 flex flex-col">
      <Header />

      <main className="flex-1 pt-20 md:pt-24">
        <div className="max-w-4xl mx-auto px-4 md:px-6 pb-10">

          {/* Hero */}
          <div className="bg-background-50 rounded-2xl overflow-hidden">
            <div className="relative h-36 md:h-44 bg-gradient-to-br from-slate-500/15 via-stone-400/10 to-amber-400/10 overflow-hidden">
              <div className="absolute inset-0 bg-background-50/30"></div>
              <div className="absolute -top-12 -right-12 w-56 h-56 rounded-full bg-slate-400/10 blur-3xl"></div>
              <div className="absolute -bottom-16 -left-16 w-64 h-64 rounded-full bg-amber-300/10 blur-3xl"></div>

              <div className="relative z-10 flex flex-col items-center justify-center h-full text-center px-4">
                <div className="w-14 h-14 md:w-16 md:h-16 rounded-2xl bg-gradient-to-br from-slate-500 to-stone-600 flex items-center justify-center mb-3">
                  <i className="ri-history-line text-background-50 text-2xl"></i>
                </div>
                <h2 className="text-xl md:text-2xl font-black text-foreground-950 mb-1">Tu Historial</h2>
                <p className="text-sm text-foreground-500">{historyStats.totalEntries} actividades registradas</p>
              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 divide-x divide-background-200">
              {[
                { label: 'Últimos 7 días', value: historyStats.last7Days, icon: 'ri-calendar-line', color: 'text-sky-500' },
                { label: 'Puntos ganados', value: `+${historyStats.totalPointsEarned}`, icon: 'ri-flashlight-line', color: 'text-amber-500' },
                { label: 'Radios', value: historyStats.radiosCount, icon: 'ri-radio-line', color: 'text-emerald-500' },
                { label: 'Concursos', value: historyStats.concursosCount, icon: 'ri-trophy-line', color: 'text-violet-500' },
              ].map((stat) => (
                <div key={stat.label} className="px-3 py-4 text-center">
                  <div className="flex items-center justify-center gap-1.5 mb-1">
                    <i className={`${stat.icon} ${stat.color} text-xs`}></i>
                    <span className="text-[10px] text-foreground-500">{stat.label}</span>
                  </div>
                  <div className="text-sm font-bold text-foreground-950">{stat.value}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Category Filters */}
          <div className="mt-5 flex flex-wrap items-center gap-1.5">
            {categoryConfig.map((cat) => (
              <button
                key={cat.key}
                onClick={() => setActiveFilter(cat.key)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                  activeFilter === cat.key
                    ? 'bg-foreground-900 text-background-50'
                    : 'bg-background-50 text-foreground-600 hover:bg-background-100 hover:text-foreground-800'
                }`}
              >
                <i className={`${cat.icon} text-xs`}></i>
                {cat.label}
              </button>
            ))}
          </div>

          {/* Result count */}
          <div className="mt-4 flex items-center gap-2">
            <span className="text-xs text-foreground-500">
              {filteredStats.count} {filteredStats.label} encontradas
            </span>
          </div>

          {/* Timeline */}
          <div className="mt-4">
            {filteredEntries.length === 0 ? (
              <EmptyTimeline filter={activeFilter} />
            ) : (
              <div className="space-y-6">
                {dayGroups.map((group) => (
                  <div key={group.label}>
                    {/* Date separator */}
                    <div className="flex items-center gap-3 mb-4">
                      <span className="text-xs font-bold text-foreground-500 uppercase tracking-wider">
                        {group.label}
                      </span>
                      <div className="flex-1 h-px bg-background-200"></div>
                    </div>

                    {/* Entries */}
                    <div className="relative">
                      <div className="absolute left-[17px] top-0 bottom-0 w-px bg-background-200"></div>
                      <div className="space-y-2">
                        {group.entries.map((entry) => {
                          const meta = typeMeta[entry.type];
                          const isExpanded = expandedId === entry.id;

                          return (
                            <div key={entry.id} className="flex gap-4 relative">
                              {/* Timeline dot */}
                              <div
                                className={`relative z-10 w-[35px] h-[35px] rounded-xl flex items-center justify-center shrink-0 ${meta.bg} border-2 border-background-50`}
                              >
                                <i className={`${meta.icon} ${meta.color} text-sm`}></i>
                              </div>

                              {/* Card */}
                              <div
                                className={`flex-1 min-w-0 bg-background-50 rounded-xl overflow-hidden transition-all ${
                                  entry.hasDetail ? 'cursor-pointer hover:bg-background-100' : ''
                                }`}
                                onClick={() => {
                                  if (entry.hasDetail) {
                                    setExpandedId(isExpanded ? null : entry.id);
                                  }
                                }}
                              >
                                <div className="p-4">
                                  <div className="flex items-start gap-3">
                                    {/* Image for radios/programas/eventos */}
                                    {entry.image && (
                                      <img
                                        src={entry.image}
                                        alt={entry.title}
                                        className="w-11 h-11 rounded-lg object-cover object-top shrink-0"
                                      />
                                    )}
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center justify-between gap-2 mb-1">
                                        <div className="flex items-center gap-2 min-w-0">
                                          <h4 className={`text-sm font-bold truncate ${entry.hasDetail ? 'text-foreground-950 group-hover:text-primary-600' : 'text-foreground-950'}`}>
                                            {entry.title}
                                          </h4>
                                          <span className="shrink-0 px-1.5 py-0.5 bg-background-100 text-foreground-500 text-[10px] font-medium rounded-md">
                                            {categoryConfig.find((c) => c.key === entry.type)?.label || entry.type}
                                          </span>
                                        </div>
                                        <span className="text-[10px] text-foreground-400 shrink-0 whitespace-nowrap">
                                          {formatTime(entry.timestamp)}
                                        </span>
                                      </div>
                                      <p className="text-xs text-foreground-500 leading-relaxed">{entry.subtitle}</p>
                                    </div>
                                  </div>

                                  {/* Expanded detail */}
                                  {isExpanded && (
                                    <div className="mt-3 pt-3 border-t border-background-100">
                                      <p className="text-xs text-foreground-600 leading-relaxed">{entry.description}</p>
                                      {entry.metadata && (
                                        <div className="mt-2 flex items-center gap-1.5">
                                          <i className="ri-information-line text-foreground-400 text-xs"></i>
                                          <span className="text-[10px] text-foreground-500">{entry.metadata}</span>
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* CTA */}
          <div className="mt-10 bg-background-50 rounded-2xl p-8 text-center">
            <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-secondary-500 flex items-center justify-center">
              <i className="ri-smartphone-line text-background-50 text-2xl"></i>
            </div>
            <h3 className="text-lg font-bold text-foreground-950 mb-2">Seguí sumando historia</h3>
            <p className="text-sm text-foreground-500 mb-5 max-w-md mx-auto">
              Cada radio que escuchás, cada concurso y cada evento suma a tu historial. Descargá la app y llevá tu experiencia al siguiente nivel.
            </p>
            <button
              onClick={openDownloadModal}
              className="px-5 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-semibold rounded-lg transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-download-2-line mr-1.5"></i>
              Descargar App RFW
            </button>
          </div>

          {/* Cross-links */}
          <div className="mt-6 flex flex-col sm:flex-row gap-3">
            <Link
              to="/oyente"
              className="flex-1 bg-background-50 rounded-2xl p-5 hover:bg-background-50 transition-colors cursor-pointer group"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-primary-500 flex items-center justify-center">
                  <i className="ri-user-line text-background-50"></i>
                </div>
                <div className="flex-1">
                  <h4 className="text-sm font-bold text-foreground-950">Tu Perfil</h4>
                  <p className="text-xs text-foreground-500">Estadísticas y actividad</p>
                </div>
                <i className="ri-arrow-right-line text-foreground-400 group-hover:text-foreground-600 transition-colors"></i>
              </div>
            </Link>
            <Link
              to="/gamificacion"
              className="flex-1 bg-background-50 rounded-2xl p-5 hover:bg-background-50 transition-colors cursor-pointer group"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-accent-500 flex items-center justify-center">
                  <i className="ri-flashlight-line text-background-50"></i>
                </div>
                <div className="flex-1">
                  <h4 className="text-sm font-bold text-foreground-950">Gamificación</h4>
                  <p className="text-xs text-foreground-500">Puntos y recompensas</p>
                </div>
                <i className="ri-arrow-right-line text-foreground-400 group-hover:text-foreground-600 transition-colors"></i>
              </div>
            </Link>
            <Link
              to="/insignias"
              className="flex-1 bg-background-50 rounded-2xl p-5 hover:bg-background-50 transition-colors cursor-pointer group"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-amber-500 flex items-center justify-center">
                  <i className="ri-award-line text-background-50"></i>
                </div>
                <div className="flex-1">
                  <h4 className="text-sm font-bold text-foreground-950">Insignias</h4>
                  <p className="text-xs text-foreground-500">Colección de logros</p>
                </div>
                <i className="ri-arrow-right-line text-foreground-400 group-hover:text-foreground-600 transition-colors"></i>
              </div>
            </Link>
          </div>

        </div>
      </main>

      <Footer />

      <div className="md:hidden h-20"></div>
    </div>
  );
}

/* ───── Empty Timeline State ───── */
function EmptyTimeline({ filter }: { filter: CategoryFilter }) {
  const cat = categoryConfig.find((c) => c.key === filter);
  return (
    <div className="bg-background-50 rounded-2xl p-10 text-center mt-6">
      <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-background-100 flex items-center justify-center">
        <i className="ri-history-line text-foreground-400 text-xl"></i>
      </div>
      <h4 className="text-sm font-bold text-foreground-700 mb-1">
        {filter === 'todas' ? 'Sin actividad' : `Sin ${cat?.label.toLowerCase()}`}
      </h4>
      <p className="text-xs text-foreground-500 max-w-xs mx-auto">
        {filter === 'todas'
          ? 'Tu historial está vacío. Empezá a escuchar radios para registrar tu actividad.'
          : `No tenés ${cat?.label.toLowerCase()} en tu historial reciente.`}
      </p>
    </div>
  );
}